import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { tokens } from '../../Common/designTokens'; // added

const ResizableContainer = ({ children, minHeight = 90, maxHeight = 500, initialHeight = 190, onHeightChange, onResizingChange }) => {
  const [height, setHeight] = useState(() => Math.max(minHeight, Math.min(maxHeight, initialHeight)));
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const dragHeightRef = useRef(height);

  const startResizing = (e) => {
    e.preventDefault();
    setIsResizing(true);
    onResizingChange?.(true);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    dragHeightRef.current = height;
  };

  const stopResizing = () => {
    if (!isResizing) return;
    setIsResizing(false);
    onResizingChange?.(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    // Commit the final height to React state and notify parent once
    const finalH = dragHeightRef.current;
    setHeight(finalH);
    onHeightChange?.(finalH);
  };

  const resize = (e) => {
    if (!isResizing || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newHeight = containerRect.bottom - e.clientY;
    const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
    dragHeightRef.current = clampedHeight;
    // Update DOM directly for smooth feedback without triggering React renders
    containerRef.current.style.height = `${clampedHeight}px`;
  };

  // Re-clamp when min/max change dynamically and sync both state and DOM height
  useEffect(() => {
    const clamped = Math.max(minHeight, Math.min(maxHeight, height));
    setHeight(clamped);
    if (containerRef.current) {
      containerRef.current.style.height = `${clamped}px`;
    }
    onHeightChange?.(clamped);
  }, [minHeight, maxHeight]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResizing);
    } else {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResizing);
    }
    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        height: `${height}px`,
        transition: isResizing ? 'none' : 'height 0.2s ease',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {/* Resize handle at the top (middle line between main and preview) */}
      <Box
        ref={resizeHandleRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '6px',
          backgroundColor: tokens.colors.bodyBorderColor50,
          cursor: 'ns-resize',
          zIndex: 1000,
          '&:hover': { backgroundColor: tokens.colors.defaultGridHoverColor },
          '&:active': { backgroundColor: tokens.palette.primaryA04 },
        }}
        onMouseDown={startResizing}
      />

      {/* Visual indicator for resizing */}
      {isResizing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: tokens.palette.primary,
            zIndex: 1001,
            boxShadow: `0 0 4px ${tokens.palette.primaryA10}`,
          }}
        />
      )}

      {/* Content below the handle */}
      <Box sx={{ height: '100%', pt: '6px' }}>
        {children}
      </Box>
    </Box>
  );
};

export default ResizableContainer;